import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { COMPANY, SERVICES } from "@/data/site";
import { useLead } from "@/context/LeadContext";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services", mega: true },
  { label: "Gallery", to: "/gallery" },
  { label: "Leak Tool", to: "/leak-detection" },
  { label: "Cost Estimator", to: "/cost-estimator" },
  { label: "Blog", to: "/blog" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const { openLead } = useLead();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setServicesOpen(false); }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass-dark py-2 shadow-lg shadow-navy/30" : "py-4 bg-gradient-to-b from-navy-deep/80 to-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <img src="/logo.png" alt="JAL RAKSHAKK Waterproofing Jaipur" className="h-11 w-11 object-contain" />
          <div className="leading-none">
            <div className="font-display font-extrabold text-white text-lg tracking-tight">JAL RAKSHAKK</div>
            <div className="text-[10px] tracking-[0.25em] text-sky uppercase">Seal Today, Safe Tomorrow</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((item) =>
            item.mega ? (
              <div key={item.to} className="relative" onMouseEnter={() => setServicesOpen(true)} onMouseLeave={() => setServicesOpen(false)}>
                <Link to={item.to} className="flex items-center gap-1 text-sm font-medium text-slate-200 hover:text-white transition-colors" data-testid="nav-services">
                  {item.label} <ChevronDown className="h-3.5 w-3.5" />
                </Link>
                {servicesOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4">
                    <div className="glass-dark rounded-md p-4 w-[560px] grid grid-cols-2 gap-1 shadow-2xl">
                      {SERVICES.slice(0, 12).map((s) => (
                        <Link key={s.slug} to={`/services/${s.slug}`} className="text-sm text-slate-300 hover:text-sky hover:bg-white/5 px-3 py-2 rounded transition-colors">
                          {s.title}
                        </Link>
                      ))}
                      <Link to="/services" className="col-span-2 text-center text-sky text-sm font-semibold py-2 hover:text-white">View all 20 services →</Link>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link key={item.to} to={item.to} className="text-sm font-medium text-slate-200 hover:text-white transition-colors">
                {item.label}
              </Link>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <button onClick={() => openLead("book_inspection")} className="btn-ripple bg-royal hover:bg-royal/90 text-white text-sm font-semibold px-5 py-2.5 rounded-sm transition-colors" data-testid="nav-book-btn">
            Book Free Inspection
          </button>
        </div>

        <button className="lg:hidden text-white" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="nav-mobile-toggle">
          {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden glass-dark mt-2 mx-4 rounded-md p-4 max-h-[75vh] overflow-y-auto" data-testid="mobile-menu">
          {NAV.map((item) => (
            <Link key={item.to} to={item.to} className="block py-3 text-slate-200 border-b border-white/10 hover:text-sky">{item.label}</Link>
          ))}
          <button onClick={() => { setOpen(false); openLead("book_inspection"); }} className="mt-4 w-full bg-royal text-white font-semibold py-3 rounded-sm">Book Free Inspection</button>
        </div>
      )}
    </header>
  );
}
